import { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import GiftIcon from '../../components/GiftIcon';
import { useVerifyServer } from '../../hooks/useVerifyServer';
import CartItem, { isEventItem } from '../../components/CartItem';
import { LangContext } from '../../contexts/LangContext';
import { api } from '../../services/api';
import './Cart.css';

export default function Cart({ cart = {}, server, lokiSkus, onClose, onRemove, onQtyChange }) {
  const history = useHistory();
  const serverMap = useVerifyServer(server);
  const { lang, t } = useContext(LangContext);
  const c = t.component.cart;
  const cartEntries = Object.values(cart);

  const [stackableIds, setStackableIds] = useState(null);

  useEffect(() => {
    api.get('payment/items/stackable')
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.items)) {
          setStackableIds(new Set(res.data.items.map(Number)));
        }
      })
      .catch(() => {});
  }, []);
  const totalValue = cartEntries.reduce((sum, { item, qty }) => sum + item.brlPriceValue * qty, 0);
  const locale = lang === 'USD' ? 'en-US' : 'pt-BR';
  const currency = lang === 'USD' ? 'USD' : 'BRL';
  const totalFormatted = totalValue.toLocaleString(locale, { style: 'currency', currency });

  const uniqueCount = cartEntries.length;

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [cardRestriction, setCardRestriction] = useState(null);

  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const response = await fetch('/data/config.json');
        const config = await response.json();
        let methods = (config[server]?.paymentMethods || []).slice();

        // As rotas verify-* passaram a exigir o token de sessao da propria conta: sem ele
        // devolviam veredito antifraude, restricao de cartao e pais de cadastro de
        // qualquer nick para qualquer um.
        const { username, token } = JSON.parse(localStorage.getItem('startParams') || '{}');

        try {
          await api.get(`payment/verify-eligibility-boacompra-card`, { params: { username, token } });
        } catch (e) {
          if (e.response?.status === 403) {
            methods = methods.filter(m => m !== 'pagseguro-card' && m !== 'fastspring');
          }
        }

        try {
          await api.get(`payment/verify-risk-eligibility`, { params: { username, token } });
        } catch (e) {
          if (e.response?.status === 403) {
            methods = methods.filter(m => m === 'picpay-pix' || m === 'omie-boleto' || m === 'xsolla');
          }
        }

        try {
          await api.get(`payment/verify-xsolla-eligibility`, { params: { username, token } });
        } catch (e) {
          if (e.response?.status === 403) {
            methods = methods.filter(m => m !== 'xsolla' && m !== 'fastspring' && m !== 'paymentwall' && m !== 'paypal');
          }
        }

        try {
          const restrictionRes = await api.get(`payment/verify-card-restriction`, { params: { username, token } });
          if (restrictionRes.data?.restricted) {
            setCardRestriction({ restricted: true, maxCardAmountCents: restrictionRes.data.maxCardAmountCents });
          }
        } catch (e) {
          // best-effort: se a checagem falhar, não bloqueia o cartão no front (o backend
          // continua sendo a barreira real na hora de cobrar — ver ProcessCard).
        }

        setPaymentMethods(methods);
      } catch (error) {
        console.error('Erro ao carregar métodos de pagamento:', error);
      }
    };

    if (server) loadPaymentMethods();
  }, [server]);

  const cardBlocked = cardRestriction?.restricted
    && Math.round(totalValue * 100) > cardRestriction.maxCardAmountCents;

  useEffect(() => {
    if (cardBlocked && selectedGateway === 'picpay-card') {
      setSelectedGateway(null);
    }
  }, [cardBlocked, selectedGateway]);

  const isGiftStackable = (gift) => {
    const giftId = Number(gift.icon.replace(/\.\w+$/, ''));
    return stackableIds === null || stackableIds.has(giftId);
  };

  const eventEntries = cartEntries.filter(({ item }) => item && isEventItem(item));

  const stackableGiftsMap = {};
  const nonStackableGiftsList = [];

  eventEntries.forEach(({ item, qty = 1 }) => {
    const isSeason = lokiSkus?.has(item.sku) ?? false;
    (item.gifts || [])
      .filter(g => g && g.icon && g.icon !== 'npextra.png')
      .forEach(gift => {
        const match = gift.name.match(/\[(\d+)\]/);
        const amountInName = match ? parseInt(match[1], 10) : 1;
        if (isGiftStackable(gift)) {
          const key = gift.icon;
          if (stackableGiftsMap[key]) {
            stackableGiftsMap[key].totalAmount += amountInName * qty;
            if (isSeason) stackableGiftsMap[key].isSeason = true;
          } else {
            stackableGiftsMap[key] = { gift, itemName: item.name, totalAmount: amountInName * qty, isSeason };
          }
        } else {
          for (let i = 0; i < qty; i++) {
            nonStackableGiftsList.push({ gift, itemName: item.name, totalAmount: amountInName, isSeason });
          }
        }
      });
  });

  const giftsArray = [...Object.values(stackableGiftsMap), ...nonStackableGiftsList];

  const calcNP = (entries) => entries
    .filter(({ item }) => item)
    .reduce((sum, { item, qty }) => {
      const rawNp = item.npPrice || item.npAmount || '0';
      return sum + (parseInt(String(rawNp).replace(/\D/g, ''), 10) || 0) * qty;
    }, 0);

  const globalNP = calcNP(cartEntries.filter(({ item }) => !lokiSkus?.has(item?.sku)));
  const seasonNP = calcNP(cartEntries.filter(({ item }) =>  lokiSkus?.has(item?.sku)));

  const SLOT_LIMIT = 120;
  const SEASON_SLOT_LIMIT = 400;

  const giftSlots = giftsArray.flatMap(({ gift, totalAmount, isSeason }) => {
    const limit = isSeason ? SEASON_SLOT_LIMIT : SLOT_LIMIT;
    const slots = [];
    let remaining = totalAmount;
    while (remaining > 0) {
      slots.push({ gift, amount: Math.min(remaining, limit), isSeason });
      remaining -= limit;
    }
    return slots;
  });

  const globalSlots  = giftSlots.filter(s => !s.isSeason);
  const seasonSlots  = giftSlots.filter(s =>  s.isSeason);
  const hasGlobal    = globalSlots.length > 0 || globalNP > 0;
  const hasSeason    = seasonSlots.length > 0 || seasonNP > 0;

  return (
    <div className="cart-page">
      {/* ── Coluna esquerda ── */}
      <div className="left-col">
        <div className="cart-title-row">
          <div className="section-title">{c.title}</div>
          <div className="continuar-btn-wrapper">
            <button className="continuar-btn" onClick={onClose}>{c.continueShopping.toUpperCase()}</button>
          </div>
        </div>

        {cartEntries.length === 0 ? (
          <div className="empty-msg">{c.empty}</div>
        ) : (
          <div className="cart-items-scroll">
            {cartEntries.map(({ item, qty }) => (
              <CartItem
                key={item.sku}
                item={item}
                qty={qty}
                server={serverMap}
                isSeason={lokiSkus?.has(item.sku) ?? false}
                onRemove={onRemove}
                onQtyChange={onQtyChange}
              />
            ))}
          </div>
        )}

        <div className="cart-divider" />

        <div className="resumo-title">{c.summaryTitle}</div>
        <div className="resumo-subtext">{c.summarySubtext}</div>

        {hasGlobal || hasSeason ? (
          <>
            {hasGlobal && (
              <div className="server-section">
                <div className="server-section-header server-section-header--global">{c.serverGlobal}</div>
                <div className="bonus-gifts-grid">
                  {globalNP > 0 && (
                    <div className="bonus-div">
                      <div className="bonus-gift-item">
                        <img src="img/np/01.png" alt="NP" className="np-img" />
                        <div className="bonus-gift-meta">
                          <span className="bonus-gift-name">{globalNP} NPs</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {globalSlots.map(({ gift, amount }, i) => {
                    const displayName = gift.name.replace(/\[\d+\]/, '').trim();
                    return (
                      <div className="bonus-div" key={i}>
                        <div className="bonus-gift-item">
                          <GiftIcon gift={gift} server={serverMap} showTooltip={false} />
                          <div className="bonus-gift-meta">
                            <span className="bonus-gift-name">[{String(amount).padStart(2, '0')}] {displayName}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {hasSeason && (
              <div className="server-section">
                <div className="server-section-header server-section-header--season">{c.serverSeason}</div>
                <div className="bonus-gifts-grid">
                  {seasonNP > 0 && (
                    <div className="bonus-div bonus-div--season">
                      <div className="bonus-gift-item">
                        <img src="img/np/01.png" alt="NP" className="np-img" />
                        <div className="bonus-gift-meta">
                          <span className="bonus-gift-name">{seasonNP} NPs</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {seasonSlots.map(({ gift, amount }, i) => {
                    const displayName = gift.name.replace(/\[\d+\]/, '').trim();
                    return (
                      <div className="bonus-div bonus-div--season" key={i}>
                        <div className="bonus-gift-item">
                          <GiftIcon gift={gift} server={serverMap} showTooltip={false} />
                          <div className="bonus-gift-meta">
                            <span className="bonus-gift-name">[{String(amount).padStart(2, '0')}] {displayName}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="no-gifts-text">
            {cartEntries.length === 0 ? c.noGiftsEmpty : c.noGiftsPacks}
          </div>
        )}
      </div>

      {/* ── Coluna direita (RESUMO) ── */}
      <div className="right-col">
        <div className="resumo-box">
          <div className="resumo-header">
            <div className="resumo-box-title">{c.resumo}</div>
          </div>
          <div className="resumo-body">
            <div className="resumo-top-row">
              <div className="resumo-qtd">
                {uniqueCount} {uniqueCount !== 1 ? c.productsPlural : c.products}
              </div>
              <span className="resumo-total-value">{totalFormatted}</span>
            </div>

            {paymentMethods.length > 0 && cartEntries.length > 0 && (
              <div className="payment-options payment-icons-wrapper" role="radiogroup" aria-label={c.finalizeDisabled}>
                {paymentMethods.map((method) => {
                  const isBlocked = method === 'picpay-card' && cardBlocked;
                  const isSelected = selectedGateway === method;
                  const hint = c.paymentHints?.[method];
                  const stateClass = isSelected ? ' is-selected' : (selectedGateway ? ' is-dimmed' : '');
                  return (
                    <button
                      key={method}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={isBlocked}
                      onClick={() => setSelectedGateway(method)}
                      className={`payment-option${stateClass}`}
                    >
                      <span className="payment-badge">
                        <img src={`img/gateway/${method}.png`} alt={method} className="gateway-img" />
                      </span>
                      {hint && <span className="payment-hint">{hint}</span>}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="finalizar-btn-wrapper">
              <button
                className="finalizar-btn"
                disabled={!selectedGateway || cartEntries.length === 0}
                onClick={() => {
                  const items = cartEntries.map(({ item, qty }) => ({ sku: item.sku, amount: qty }));
                  localStorage.setItem('cartItems', JSON.stringify(items));
                  history.push(`/${server}/checkout/${selectedGateway}`);
                }}
              >
                {selectedGateway ? c.finalize : c.finalizeDisabled}
              </button>
            </div>

            <div className="payment-note-wrapper">
              <div className="payment-note-inner">
                <div className="payment-note">{cardBlocked ? c.cardRestrictedNote : c.paymentNote}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
